import {doc, getDoc, setDoc, Timestamp} from 'firebase/firestore';
import {db} from '../firebase';

/**
 * Per-provider favorite languages.
 *
 * Local layer:  localStorage (synchronous reads so any open language picker can
 *               render the favorites group on first paint).
 * Remote layer: Firestore User/{userId}.languageFavorites (cross-device sync).
 *
 * The service is reactive: components subscribe via `subscribe()` and re-render
 * whenever the list changes, so starring a language in one open picker reorders
 * every other open picker in the same session immediately.
 *
 * Call syncFromFirebase() on login, syncToFirebase() on toggle,
 * clearLocalCache() on sign-out.
 */

const LS_KEY = 'language_favorites';
const FIRESTORE_FIELD = 'languageFavorites';

const trim = (value: unknown) => String(value ?? '').trim();

const sanitize = (input: unknown): string[] => {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input) {
    const name = trim(raw);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    result.push(name);
  }
  return result;
};

class LanguageFavoritesService {
  private cache: string[] | null = null;

  private listeners = new Set<() => void>();

  private read(): string[] {
    if (this.cache) return this.cache;
    try {
      this.cache = sanitize(JSON.parse(localStorage.getItem(LS_KEY) || '[]'));
    } catch {
      this.cache = [];
    }
    return this.cache;
  }

  private write(next: string[]) {
    this.cache = next;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
      // localStorage can throw in private mode / quota edge cases; the
      // in-memory cache still drives the current session, which is a safe
      // degradation rather than a crash.
    }
    this.emit();
  }

  private emit() {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch {
        /* a misbehaving subscriber must not break the others */
      }
    });
  }

  // ── Reads ────────────────────────────────────────────────────

  getFavorites(): string[] {
    // Hand back a copy so callers can't mutate the cache in place.
    return [...this.read()];
  }

  // Stable snapshot for `useSyncExternalStore`: returns the cached array by
  // reference (a new array is only ever created on a real change in `write`),
  // so React doesn't see a "change" on every render and loop forever.
  getSnapshot = (): string[] => this.read();

  isFavorite(language: unknown): boolean {
    const name = trim(language);
    return Boolean(name) && this.read().includes(name);
  }

  // ── Writes ───────────────────────────────────────────────────

  // Toggles a language's favorite state. Newly favorited languages are
  // appended so the group stays in the order the provider starred them;
  // callers decide how to present that order.
  toggleFavorite(language: unknown): boolean {
    const name = trim(language);
    if (!name) return false;
    const current = this.read();
    const isFav = current.includes(name);
    this.write(
      isFav ? current.filter(item => item !== name) : [...current, name],
    );
    return !isFav;
  }

  setFavorites(languages: unknown) {
    this.write(sanitize(languages));
  }

  // ── Reactivity ───────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // ── Firebase sync ────────────────────────────────────────────

  async syncToFirebase(userId: unknown) {
    const id = trim(userId);
    if (!id) return;
    try {
      await setDoc(
        doc(db, 'User', id),
        {
          [FIRESTORE_FIELD]: {
            languages: this.getFavorites(),
            lastUpdated: Timestamp.now(),
          },
        },
        {merge: true},
      );
    } catch (err) {
      console.error('LanguageFavorites: Firestore write failed:', err);
    }
  }

  async syncFromFirebase(userId: unknown) {
    const id = trim(userId);
    if (!id) return;
    try {
      const snap = await getDoc(doc(db, 'User', id));
      const remote = snap.exists()
        ? snap.data()?.[FIRESTORE_FIELD]?.languages
        : null;
      if (Array.isArray(remote)) {
        this.write(sanitize(remote));
      }
    } catch (err) {
      console.error('LanguageFavorites: Firestore read failed:', err);
    }
  }

  // ── Cache management ─────────────────────────────────────────

  clearLocalCache() {
    this.cache = [];
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* noop */
    }
    this.emit();
  }
}

export const languageFavorites = new LanguageFavoritesService();
